import React, {
  useState, useCallback, useReducer, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { Tooltip, TooltipButton, IconInfo } from '@americanexpress/dls-react';
import { validateDlsInput } from '../../Validations/Validations';
import { parseHtml } from '../../utils/util';
import { getRequiredData } from '../../constants';

const getVal = require('lodash/get');

/**
 * returns DLS TextArea component
 * @param {*} props
 */
// eslint-disable-next-line complexity -- desc
const TextArea = (props) => {
  const {
    cssProperties,
    fieldType,
    fieldLabel,
    fieldPlaceholder,
    fieldId,
    fieldValidation,
    filledFormData,
    memberInfo,
    selectKey,
    preFilledWith,
    isPreFilled,
    fetchUrl,
    fieldRank,
    validationErrors,
    setValidationErrors,
    setFocus,
    fieldValue,
    validations = [],
    curPageValues,
    setCurPageValues,
    comments,
    setComments,
    entityToEdit,
    entityComments,
    entityCompositeKey,
    idsToClear,
    isScrollTop = true,
  } = props;
  const [data, setData] = useState('');
  const [isValid, setIsValid] = useState(true);
  let dlsClass = 'form-control';
  const [validationErrState, dispatchErrState] = useReducer((prevState, payload) => ({ ...prevState, ...payload }), { validationErr: '', isValid: true });
  const optionalLabel = fieldLabel && Object.prototype.hasOwnProperty.call(fieldLabel, 'optionalLabel') ? fieldLabel.optionalLabel : ' (Optional)';
  // if background color change is required
  if (fieldValidation.bgColor) {
    dlsClass = `${dlsClass} ${fieldValidation.bgColor}`;
  }

  // Back button support
  useEffect(() => {
    if (filledFormData && filledFormData[fieldId]) {
      setData(filledFormData[fieldId]);
      curPageValues[fieldId] = filledFormData[fieldId];
      setCurPageValues({ ...curPageValues });
    } else {
      setData('');
    }
  }, [fieldId]);

  // TODO: logic update will require when data is needed from API in future
  useEffect(() => {
    let preFilledVal = '';
    if (isPreFilled && preFilledWith === 'filledFormData' && !fetchUrl) {
      if (filledFormData) {
        preFilledVal = filledFormData[selectKey];
        setData(preFilledVal);
      }
    } else if (isPreFilled && preFilledWith === 'memberInfo' && !fetchUrl) {
      if (memberInfo) {
        // we have to combine the data from memberInfo for below keys
        preFilledVal = getRequiredData({
          isPreFilled, preFilledWith, fieldValue, selectKey, fetchUrl, filledFormData, memberInfo,
        });
        setData(preFilledVal);
      }
    } else if (fieldValue !== 'default' && fieldValue !== '') {
      preFilledVal = fieldValue;
      setData(preFilledVal);
    }
  }, [memberInfo, filledFormData]);

  /**
   * handle input value
   * @param {*} event
   */
  const handleInput = (event, val = null) => {
    // console.log(event.target.value);
    if (comments && comments[fieldId]) {
      // This isValid is for showing red borders for the comments from the review tool.
      setIsValid(true);
      delete comments[fieldId];
      setComments({ ...comments });
    }

    if (event) {
      setIsValid(true);
    } else if (entityComments.length > 0
      && entityCompositeKey
      && Object.keys(entityComments.filter(
        (obj) => obj.compositeKey === entityCompositeKey)).length > 0
      && Object.keys(entityComments.filter(
        (obj) => obj.compositeKey === entityCompositeKey)[0]).includes(fieldId)) {
      // This isValid is for showing red borders for the comments from the review tool.
      setIsValid(false);
    }

    const inputVal = event ? event.target.value : val;
    if (validations.length > 0) {
      const invalidMsg = validateDlsInput(inputVal, validations);
      if (invalidMsg) {
        dispatchErrState({ validationErr: `${invalidMsg}`, isValid: false });
      } else {
        dispatchErrState({ validationErr: '', isValid: true });
      }
    }
    setData(inputVal);
    setCurPageValues({ ...curPageValues, [fieldId]: inputVal });

    if (comments && comments[fieldId]) {
      delete comments[fieldId];
      setComments({ ...comments });
    }

    setIsValid(true);
  };

  const handleOnBlur = (e) => {
    if (e?.target?.value) {
      setData(() => e.target.value.trim());
    }
  };

  useEffect(() => {
    setIsValid(true);
    if (entityToEdit && typeof entityToEdit[fieldId] !== 'undefined') {
      handleInput(null, entityToEdit[fieldId]);
    }
  }, [entityToEdit]);

  useEffect(() => {
    if (comments && comments[fieldId]) {
      setIsValid(false);
    }
  }, [comments]);

  const inputFocus = useCallback((inputElement) => {
    if (inputElement) {
      // inputElement.focus();
      if (isScrollTop) {
        window.scrollTo(0, 0);
      }
    }
  }, []);

  const invalidHandler = (e) => {
    // e.target.setCustomValidity(' ');
    if (validations.length > 0) {
      e.preventDefault();
      handleInput(e);
      setFocus({ ...validationErrors, ...{ [fieldRank]: `${fieldId}` } });
    }
  };

  useEffect(() => {
    // validationErr also can change multiple times, so need one isValid
    if (validationErrState.isValid && validationErrors[fieldRank]) {
      delete validationErrors[fieldRank];
      setValidationErrors({ ...validationErrors });
    } else if (!validationErrState.isValid) {
      setValidationErrors({ ...validationErrors, ...{ [fieldRank]: `${fieldId}` } });
    }
  }, [validationErrState.isValid]);

  useEffect(() => {
    if (getVal(idsToClear.ids, fieldId)) setData('');
  }, [idsToClear.toggleState]);

  const renderElement = (
    <textarea
      rows={cssProperties.rows}
      type={fieldType}
      id={fieldId}
      name={fieldId}
      className={dlsClass}
      value={data}
      placeholder={fieldPlaceholder}
      onChange={handleInput}
      onBlur={handleOnBlur}
      disabled={fieldValidation.disabled}
      required={fieldValidation.required}
      ref={inputFocus}
      maxLength={fieldValidation.maxSize}
      onInvalid={invalidHandler}
      style={{
        border: !isValid && data ? '.0625rem solid red' : '.0625rem solid #8e9092',
      }}
    />
  );

  return (
    <>
      {
        fieldLabel && fieldLabel.label && (
          <label className={fieldLabel.cssProperties.style} htmlFor={fieldId}>
            {parseHtml(fieldLabel.label)}
          </label>
        )
      }
      {!fieldValidation.required && optionalLabel && (
        <span
          className={fieldLabel.cssProperties.style}
        >
          {optionalLabel}
        </span>
      )}
      {fieldValidation && fieldValidation.isToolTip
        && (
          <>
            <span>{' '}</span>
            <Tooltip id="ToolTip" triggerElement={<TooltipButton icon={<IconInfo size="sm" title="info" />} />}>{fieldValidation.toolTipInfo}</Tooltip>
          </>
        )}
      {renderElement}
      {fieldValidation.disabled && (
        <input type="hidden" id={fieldId} name={fieldId} value={data} />
      )}
      {
        validationErrState.validationErr
        && (
          <div id="alert-error" className={cssProperties.validationErrorStyle ? cssProperties.validationErrorStyle : 'alert dls-color-warning pad-1-t col-md-12'}>
            <span className="icon icon-sm dls-icon-warning-filled pad-1-r" />
            <span>{validationErrState.validationErr}</span>
          </div>
        )
      }
      {
        comments && comments[fieldId]
        && (
          <div id="alert-error" className="alert dls-color-warning pad-1-t">
            <span className="icon icon-sm dls-icon-warning-filled pad-1-r" />
            <span>{comments[fieldId].comment}</span>
          </div>
        )
      }
      {
        entityComments.length > 0
        && entityCompositeKey
        && Object.keys(entityComments.filter(
          (obj) => obj.compositeKey === entityCompositeKey)).length > 0
        && Object.keys(entityComments.filter(
          (obj) => obj.compositeKey === entityCompositeKey)[0]).includes(fieldId)
        && !isValid
        && (
          <div id="alert-error" className="alert dls-color-warning pad-1-t">
            <span className="icon icon-sm dls-icon-warning-filled pad-1-r" />
            <span>{
              fieldId === 'compositeKey' ? entityComments.filter((obj) => obj.compositeKey === entityCompositeKey)[0][fieldId]
                : entityComments.filter(
                  (obj) => obj.compositeKey === entityCompositeKey)[0][fieldId].defaultValue
            }
            </span>
          </div>
        )
      }
    </>
  );
};

TextArea.propTypes = {
  fieldType: PropTypes.string,
  cssProperties: PropTypes.objectOf(PropTypes.shape),
  fieldLabel: PropTypes.objectOf(PropTypes.shape),
  fieldPlaceholder: PropTypes.string,
  fieldId: PropTypes.string,
  fieldValidation: PropTypes.objectOf(PropTypes.shape),
  preFilledWith: PropTypes.string,
  fetchUrl: PropTypes.string,
  filledFormData: PropTypes.objectOf(PropTypes.shape),
  memberInfo: PropTypes.objectOf(PropTypes.shape),
  selectKey: PropTypes.string,
  isPreFilled: PropTypes.string,
  setValidationErrors: PropTypes.func,
  validationErrors: PropTypes.objectOf(PropTypes.shape),
  validations: PropTypes.arrayOf(PropTypes.shape),
  setFocus: PropTypes.func,
  fieldRank: PropTypes.number,
  curPageValues: PropTypes.objectOf(PropTypes.shape),
  setCurPageValues: PropTypes.func,
  fieldValue: PropTypes.string,
  comments: PropTypes.objectOf(PropTypes.shape),
  setComments: PropTypes.func,
  entityToEdit: PropTypes.objectOf(PropTypes.shape),
  entityComments: PropTypes.objectOf(PropTypes.shape),
  entityCompositeKey: PropTypes.string,
  idsToClear: PropTypes.objectOf(PropTypes.shape),
  isScrollTop: PropTypes.bool,
};

export default TextArea;
